import {t} from 'ttag';
import cx from "classnames";
import PropTypes from 'prop-types';
import React, {useEffect, useState} from "react";
import {Container} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import useStyles from './RouteSelectorStyles';
import {AirportSelector} from "./index";
import SyncAltIcon from "../../Icons/SyncAltIcon";

const RouteSelector = ({airportsData, autoFocus, variant, onChange, originPlaceholder, destinationPlaceholder, optionField, compressed, originFocus, destinationFocus, onBlur, onFocus, originError, destinationError, error, ...props}) => {
    let destinationInput;
    let originInput;
    let switching = false;

    const classes = useStyles(error);

    /**
     * Updates destinations airport list based on provided airport
     *
     * @param {object} airport
     * @returns {[]}
     */
    const filterDestinations = airport => {
        return (airport && airportsData.filter(a => airport.destinations.includes(a.code))) || [];
    };

    const [origin, setOrigin] = useState(props.origin);
    const [destinations, setDestinations] = useState(filterDestinations(origin));
    const [destination, setDestination] = useState(props.destination);
    const [originFocused, setOriginFocused] = useState(originFocus);
    const [destinationFocused, setDestinationFocused] = useState(destinationFocus);

    useEffect(() => {
        setOrigin(props.origin);
        // eslint-disable-next-line
    }, [props.origin]);

    useEffect(() => {
        setDestination(props.destination);
        // eslint-disable-next-line
    }, [props.destination]);

    useEffect(() => {
        setOriginFocused(originFocus);
        if (originFocus && originInput) originInput.focus()
        // eslint-disable-next-line
    }, [originFocus]);

    useEffect(() => {
        setDestinationFocused(destinationFocus);
        if (destinationFocus && destinationInput) destinationInput.focus()
        // eslint-disable-next-line
    }, [destinationFocus]);

    useEffect(() => {
        // eslint-disable-next-line
        setDestinations(filterDestinations(props.origin));
        // eslint-disable-next-line
    }, [airportsData]);

    /**
     * Origins selector change handler
     * @param {object} value
     */
    const handleOriginChange = value => {
        setOrigin(value);
        setDestination(null);
        if (originInput) originInput.blur();
        setDestinations(filterDestinations(value));
        if (destinationInput && !switching) destinationInput.focus();
    };

    /**
     * Destinations selector change handler
     * @param {object} value
     */
    const handleDestinationChange = value => {
        setDestination(value);
        if(destinationInput) destinationInput.blur();
        if (!switching && onChange) onChange({origin, destination: value});
        switching = false;
    };

    /**
     * Switching origin for destination handler
     */
    const handleRouteSwitch = () => {
        if (!origin || ! destination) return;
        switching = true;
        handleOriginChange(destination);
        handleDestinationChange(origin);
        if (onChange) onChange({origin: destination, destination: origin});
    };

    /**
     * Origin selector component
     *
     * @returns {JSX.Element}
     */
    const originSelector = () => {
        return <AirportSelector
            onChange={handleOriginChange}
            autoFocus={autoFocus}
            label={t`From`}
            placeholder={originPlaceholder}
            name={'origin'}
            options={airportsData}
            variant={'azo'}
            leftRadius={variant !== 'stacked'}
            onFocus={e => {
                setOriginFocused(true)
                onFocus(e);
            }}
            onBlur={e => {
                setOriginFocused(false);
                if (!destinationFocus) onBlur(e);
            }}
            inputRef={input => originInput = input}
            value={origin}
            optionField={optionField}
            compressed={compressed}
            error={originError || error}
        />;
    }

    /**
     * Destination selector component
     *
     * @returns {JSX.Element}
     */
    const destinationSelector = () => {
        return <AirportSelector
            label={t`To`}
            placeholder={destinationPlaceholder}
            name={'destination'}
            options={destinations}
            variant={'azo'}
            rightRadius={variant !== 'stacked'}
            onFocus={e => setDestinationFocused(true)}
            onBlur={e => {
                setDestinationFocused(false);
                if (!originFocus) onBlur(e);
            }}
            inputRef={input => destinationInput = input}
            noOptionsText={origin ? t`No airports found.` : t`Please select an origin first.`}
            onChange={handleDestinationChange}
            value={destination}
            optionField={optionField}
            compressed={compressed}
            error={destinationError || error}
        />;
    }

    /**
     * Regular component render
     *
     * @returns {JSX.Element}
     * @constructor
     */
    const Normal = () => (
        <div className={cx('azo-route-selector', classes.root)}>
            <div className={classes.item}>
                {originSelector()}
            </div>
            <div className={cx(classes.leftMiddleItem, {focused: originFocused})}>
                <div className={classes.exchangeIcon}>
                    <IconButton onClick={handleRouteSwitch} aria-label={'exchange-route'} color={'secondary'}>
                        <SyncAltIcon/>
                    </IconButton>
                </div>
            </div>
            <div className={cx(classes.rightMiddleItem, {focused: destinationFocused})}/>
            <div className={classes.item}>
                {destinationSelector()}
            </div>
        </div>
    );

    /**
     * Origin and destination are stack on top of each other. Used im mobile devices
     *
     * @returns {JSX.Element}
     * @constructor
     */
    const Stacked = () => (
        <div className={cx("azo-route-selector")}>
            {originSelector()}
            <div className={classes.stacked}>
                {destinationSelector()}
            </div>
        </div>
    );

    /**
     *
     * @returns {JSX.Element}
     * @constructor
     */
    const Small = () => (
        <Container maxWidth={'md'} disableGutters>
            {Normal()}
        </Container>
    );

    switch (variant) {
        case 'stacked':
            return <>{Stacked()}</>;

        case 'small':
            return <>{Small()}</>;

        case 'standard':
        default:
            return <>{Normal()}</>;
    }
};

RouteSelector.propTypes = {
    airportsData: PropTypes.arrayOf(AirportSelector.airportShape).isRequired,
    originPlaceholder: PropTypes.string,
    destinationPlaceholder: PropTypes.string,
    autoFocus: PropTypes.bool,
    onChange: PropTypes.func,
    variant: PropTypes.oneOf(['normal', 'stacked', 'small']),
    origin: PropTypes.object,
    destination: PropTypes.object,
    optionField: PropTypes.oneOf(['name', 'code']),
    compressed: PropTypes.bool,
    originFocus: PropTypes.bool,
    destinationFocus: PropTypes.bool,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    originError: PropTypes.bool,
    destinationError: PropTypes.bool,
    error: PropTypes.bool,
};

RouteSelector.defaultProps = {
    autoFocus: false,
    variant: 'normal',
    origin: null,
    destination: null,
    optionField: 'name',
    compressed: false,
    originFocus: false,
    destinationFocus: false,
    onBlur: e => null,
    onFocus: e => null,
    originError: false,
    destinationError: false,
    error: false,
};

export default RouteSelector;
